import React, { useEffect, useState } from "react";
import type { Course } from "@nelurio/contracts";
import { Button, Card, EmptyState, Field, PageHeader, SelectField } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api, isDemoMode } from "../api/client";
import { demoCourses } from "../demo/data";
import { CourseCard } from "../components/CourseCard";
import { ColorPicker } from "../components/ColorPicker";

export function CoursesPage() {
  const { t } = useI18n();
  const [courses, setCourses] = useState<Course[]>(isDemoMode() ? demoCourses : []);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("#6c4cf6");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isDemoMode()) return;

    let active = true;

    api<Course[] | null>("/courses")
      .then((response) => {
        if (active) {
          setCourses(Array.isArray(response) ? response : []);
          setError("");
        }
      })
      .catch((reason) => {
        if (active) {
          setCourses([]);
          setError(reason instanceof Error ? reason.message : t("common.error"));
        }
      });

    return () => {
      active = false;
    };
  }, [t]);

  const createCourse = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    const draft: Course = {
      id: crypto.randomUUID(),
      title: title.trim(),
      code: code.trim(),
      color,
      icon: "N",
      progress: 0,
      active: true,
      resourceCount: 0,
      dueReviewCount: 0
    };

    try {
      if (isDemoMode()) {
        setCourses((items) => [draft, ...items]);
      } else {
        const createdCourse = await api<Course>("/courses", {
          method: "POST",
          body: JSON.stringify(draft)
        });
        setCourses((items) => [createdCourse, ...items]);
      }

      setTitle("");
      setCode("");
      setColor("#6c4cf6");
      setShowForm(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("common.error"));
    }
  };

  return (
    <div className="page-stack">
      <PageHeader
        title={t("course.title")}
        subtitle={courses.length ? t("courses.subtitleSome") : t("course.noCourses")}
        actions={
          <Button icon="plus" onClick={() => setShowForm((value) => !value)}>
            {t("course.addCourse")}
          </Button>
        }
      />

      {showForm && (
        <Card className="inline-form">
          <form onSubmit={createCourse}>
            <Field
              label={t("task.title")}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
            <Field
              label={t("course.codeField")}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <div className="nl-field">
              <span className="nl-field__label">{t("course.colorField")}</span>
              <ColorPicker value={color} onChange={setColor} />
            </div>
            <div className="inline-form__actions">
              <Button type="submit">{t("common.create")}</Button>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {error && <div className="page-error">{error}</div>}

      {courses.length > 0 ? (
        <div className="course-grid course-grid--page">
          {courses.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon="book"
            title={t("course.noCourses")}
            action={
              <Button onClick={() => setShowForm(true)}>
                {t("course.addCourse")}
              </Button>
            }
          />
        </Card>
      )}
    </div>
  );
}
